import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useSpeechInput - wraps the browser's native SpeechRecognition. No dependency, no server
 * call: unsupported browsers (Firefox, most non-Chromium) just get `supported: false` and
 * the caller falls back to typing.
 */
export default function useSpeechInput({ onResult, enabled = true, sessionKey } = {}) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const callbackRef = useRef(onResult);
  callbackRef.current = onResult;
  const Recognition = typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const cancel = useCallback(() => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (!recognition) return;
    recognition.onresult = null;
    recognition.onend = null;
    recognition.onerror = null;
    try { if (recognition.abort) recognition.abort(); else recognition.stop(); } catch { /* already stopped */ }
  }, []);
  useEffect(() => {
    cancel();
    setListening(false);
    return cancel;
  }, [enabled, sessionKey, cancel]);

  const start = () => {
    if (!Recognition || !enabled || recognitionRef.current) return;
    const recognition = new Recognition();
    recognitionRef.current = recognition;
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      if (recognitionRef.current !== recognition) return;
      const result = event.results?.[event.resultIndex || 0];
      const transcript = result?.[0]?.transcript;
      if (result?.isFinal !== false && transcript?.trim()) callbackRef.current?.(transcript.trim());
    };
    const finish = () => {
      if (recognitionRef.current !== recognition) return;
      cancel(); setListening(false);
    };
    recognition.onend = finish;
    recognition.onerror = finish;
    try { recognition.start(); setListening(true); } catch { finish(); }
  };

  const stop = () => {
    cancel();
    setListening(false);
  };

  return { supported: Boolean(Recognition), listening, start, stop };
}
